import {
  Alert,
  Autocomplete,
  Box,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import TestTeamExternalIntegration from 'components/rbac-settings/teams/TestTeamExternalIntegration'
import { isObservabilityToolOfType } from 'components/status-checks/authentication-form-validation-utils'
import { MonitorUrlFieldInput } from 'components/status-checks/form/status-check/common/MonitorUrlFieldInput'
import { StatusCheckSuccessEvaluation } from 'components/status-checks/form/status-check/StatusCheckSuccessEvaluation'
import { useGetExistingStatusCheckByMonitorUrl } from 'components/status-checks/hooks/use-get-existing-status-check-by-monitor-url'
import {
  buildTeamExternalIntegration,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

const DEFAULT_CONTENT_TYPE_OPTIONS = [
  'application/json',
  'text/plain',
  'text/xml'
]

const fieldsToWatch = [
  'endpointConfiguration',
  'externalIntegration',
  'monitorUrl',
  'observabilityTool'
] as const

/**
 * Renders help text which links to our docs on request body interpolation values.
 */
const RequestPayloadHelperText: React.FC = () => {
  return (
    <Typography color="textSecondary" variant="caption">
      Request bodies can contain{' '}
      <MUIExternalLink
        href="https://www.gremlin.com/docs/platform-health-checks-custom"
        sx={{ alignItems: 'center', columnGap: 0.5, display: 'inline-flex' }}
      >
        Interpolation Values <Icon icon="arrowUpRightFromSquare" />
      </MUIExternalLink>
    </Typography>
  )
}

export const StatusCheckNameMonitorSuccessEvaluation: React.FC = () => {
  const { control, formState, getFieldState, getValues, register, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [
    endpointConfiguration,
    externalIntegration,
    monitorUrl,
    observabilityTool
  ] = useWatch({
    control,
    name: fieldsToWatch
  })

  const endpointConfigurationField = getFieldState('endpointConfiguration')
  const monitorUrlField = getFieldState('monitorUrl')
  const statusChecksUrls = getStatusChecksUrls()
  const existingStatusCheck = useGetExistingStatusCheckByMonitorUrl(
    observabilityTool,
    monitorUrl,
    endpointConfiguration?.method
  )

  const isAwsObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.AWS,
    observabilityTool
  )
  const isCustomObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.CUSTOM,
    observabilityTool
  )
  const isPostMethod = endpointConfiguration?.method === 'POST'

  const {
    isTestStatusCheckDisabled,
    handleSuccess,
    handleTestConnectionError,
    setEndpointResponse,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  // Re-validate the Monitor URL field when the request method changes
  useEffect(() => {
    if (!monitorUrlField.isDirty) return

    // Trigger re-validation of the Monitor URL field when relevant dependencies change
    trigger('monitorUrl')
  }, [endpointConfiguration?.method, monitorUrlField.isDirty, trigger])

  useEffect(() => {
    if (!endpointConfigurationField.isDirty && !monitorUrlField.isDirty) return

    setIsStatusCheckConnectionValidated(false)
    setIsStatusCheckEvaluationValidated(false)
  }, [
    endpointConfigurationField.isDirty,
    monitorUrlField.isDirty,
    setEndpointResponse,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  ])

  return (
    <Stack spacing={3}>
      <FormControl>
        <Typography mb={2}>What is the name of this Health Check?</Typography>
        <TextField
          data-testid="status-check-monitor-name-input"
          error={Boolean(formState.errors.statusCheckName)}
          helperText={formState.errors.statusCheckName?.message}
          label="Health Check Name"
          variant="outlined"
          {...register('statusCheckName')}
        />
      </FormControl>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Typography mb={2}>
          What monitor or alert will help Gremlin determine the health of your
          Service?
        </Typography>
        <Stack direction="row" spacing={1}>
          {isCustomObservabilityTool && (
            <FormControl>
              <Controller
                control={control}
                name="endpointConfiguration.method"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      {...field}
                      select
                      data-testid="status-check-monitor-method-select"
                      error={Boolean(error)}
                      helperText={error?.message}
                      label="method"
                      sx={{ minWidth: 120 }}
                      onChange={(event) => {
                        const { value } = event.target

                        field.onChange(value)
                      }}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                    </TextField>
                  )
                }}
              />
            </FormControl>
          )}
          <FormControl sx={{ flexGrow: 1 }}>
            <Controller
              control={control}
              name="monitorUrl"
              render={({ field, fieldState }) => (
                <MonitorUrlFieldInput
                  externalIntegration={externalIntegration}
                  field={field}
                  fieldState={fieldState}
                  label="Monitor or Alert URL"
                  {...(existingStatusCheck?.id && {
                    to: statusChecksUrls.edit(existingStatusCheck.id)
                  })}
                />
              )}
            />
          </FormControl>
        </Stack>
        {isCustomObservabilityTool && (
          <>
            {existingStatusCheck && (
              <Box sx={{ pb: isPostMethod ? 0.5 : 0 }}>
                <Alert severity="warning">
                  This Monitor URL is configured for use by another Health
                  Check. You can continue creating this Health Check, or search
                  existing Health Checks to ensure no duplicates are created.
                </Alert>
              </Box>
            )}
            {isPostMethod && (
              <>
                <FormControl>
                  <Controller
                    control={control}
                    name="endpointConfiguration.headers.content-type"
                    render={({ field }) => {
                      return (
                        <Autocomplete<string, false, false, true>
                          autoComplete
                          freeSolo
                          options={DEFAULT_CONTENT_TYPE_OPTIONS}
                          renderInput={(params) => {
                            const { headers: headersError } =
                              formState.errors.endpointConfiguration ?? {}

                            return (
                              <TextField
                                {...field}
                                {...params}
                                data-testid="status-check-monitor-request-content-type-input"
                                error={Boolean(headersError?.['content-type'])}
                                helperText={
                                  headersError?.['content-type']?.message
                                }
                                inputProps={{
                                  ...params.inputProps,
                                  maxLength: 50
                                }}
                                label="Content Type"
                                name="endpointConfiguration.headers.content-type"
                                variant="outlined"
                              />
                            )
                          }}
                          value={field.value ?? ''}
                          onChange={(_, value) => {
                            field.onChange(value)
                          }}
                        />
                      )
                    }}
                  />
                </FormControl>
                <FormControl>
                  <Controller
                    control={control}
                    name="endpointConfiguration.payload"
                    render={({ field }) => {
                      const { payload: payloadError } =
                        formState.errors.endpointConfiguration ?? {}

                      return (
                        <TextField
                          {...field}
                          multiline
                          data-testid="status-check-monitor-request-body-input"
                          error={Boolean(payloadError)}
                          helperText={
                            payloadError?.message ?? (
                              <RequestPayloadHelperText />
                            )
                          }
                          label="Request Body"
                          maxRows={10}
                          minRows={4}
                          variant="outlined"
                        />
                      )
                    }}
                  />
                </FormControl>
              </>
            )}
          </>
        )}
        {(isCustomObservabilityTool || isAwsObservabilityTool) && (
          <Stack mt={2} spacing={3}>
            <TestTeamExternalIntegration
              authenticate={false}
              buttonText="Test Connection"
              disabled={isTestStatusCheckDisabled}
              endpointConfiguration={endpointConfiguration}
              integration={buildTeamExternalIntegration(getValues())}
              onError={handleTestConnectionError}
              onSuccess={handleSuccess}
            />
            <StatusCheckSuccessEvaluation />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
